/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { convertToString } from 'app/shared/lib/utilities';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';
import { signalNames } from 'app/shared/lib/signals';

@autoinject
export class TickValueConverter {
    public signals: string[] = [signalNames.clockTickSecond];

    // use |tick to force a value to update every second (such as time remaining calculations)
    public toView(value: unknown): any {
        return value;
    }
}

@autoinject
export class RelativeDateTimeValueConverter {

    public signals: string[] = [signalNames.timeZoneChanged, signalNames.languageChanged, signalNames.clockTickMinute];

    constructor(private readonly formatProvider: FormatProvider) { }

    public toView(value: unknown, format: string = '', seconds?: boolean): string | null {
        if(typeof value === 'string') {
            return this.formatProvider.formatRelativeDateTime(value, { format, seconds: seconds || false });
        }
        return null;
    }
}

@autoinject
export class DateFormatValueConverter {

    public signals: string[] = [signalNames.timeZoneChanged, signalNames.languageChanged];

    constructor(private readonly formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown): string | null {
        if(typeof value === 'string' || value instanceof Date || value instanceof DateValue || value instanceof DateTimeValue) {
            return this.formatProvider.formatDate(value, { format: convertToString(format) });
        }

        return null;
    }

}

@autoinject
export class DateTimeFormatValueConverter {

    public signals: string[] = [signalNames.timeZoneChanged, signalNames.languageChanged];

    constructor(private readonly formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown, timeZone?: unknown): string | null {
        if(typeof value === 'string' || value instanceof Date || value instanceof DateValue || value instanceof DateTimeValue) {
            return this.formatProvider.formatDateTime(value, {
                format: convertToString(format),
                timeZone: convertToString(timeZone)
            });
        }

        return null;
    }

}

@autoinject
export class DateOnlyValueConverter {

    constructor(private readonly env: EnvironmentService) { }

    public toView(strDateTime: string | null, timeZone?: string): string | null {
        if(!strDateTime) { return null; }

        const dateTime: DateTimeValue = new DateTimeValue(strDateTime);
        if(!dateTime.isValid()) { return null; }

        return dateTime.toDate(timeZone || this.env.displayTimeZone).toString() || null;
    }

}

@autoinject
export class TimeFormatValueConverter {

    public signals: string[] = [signalNames.timeZoneChanged, signalNames.languageChanged];

    constructor(private readonly formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown, timeZone?: unknown, time24?: boolean): string | null {
        if(typeof value === 'string' || value instanceof Date || value instanceof DateValue || value instanceof DateTimeValue) {
            return this.formatProvider.formatTime(value, {
                format: convertToString(format),
                timeZone: convertToString(timeZone),
                time24: time24
            });
        }

        return null;
    }

}

@autoinject
export class NowValueConverter {

    public signals: string[] = [signalNames.clockTickMinute];

    constructor(private readonly env: EnvironmentService) { }

    public toView(): string {
        return this.env.now();
    }

}

@autoinject
export class OverdueValueConverter {

    public signals: string[] = [signalNames.clockTickMinute];

    constructor(private readonly env: EnvironmentService) { }

    public toView(value: string, completed: boolean = false): boolean | null {
        if(value === null || value === undefined) { return null; }
        if(completed) { return false; }

        // date
        if(value.length === 10) {
            const dateValue = new DateValue(value);
            const today = new DateValue(this.env.today);
            if(!dateValue.isValid() || !today.isValid()) { return null; }
            if(dateValue.isEqual(today)) { return false; }
            return !dateValue.isAfter(today);
        }
        // date/time
        const dateTimeValue = new DateTimeValue(value);
        const now = new DateTimeValue(this.env.now());
        if(!dateTimeValue.isValid() || !now.isValid()) { return null; }
        return !dateTimeValue.isAfter(now);
    }

}

/**
 * Calculate the number of days until or since a specified datetime.
 * @param value A string that can be converted to a DateTimeValue.
 * @returns A positive or negative integer, 0, or null.
 */
@autoinject
export class DaysUntilValueConverter {

    public signals: string[] = [signalNames.clockTickMinute];

    constructor(private readonly env: EnvironmentService) { }

    public toView(value: string): number | null {
        if(!value) { return null; }

        const now = new DateTimeValue(this.env.now());
        const other = new DateTimeValue(value);

        if(!other.isValid() || !now.isValid()) { return null; }

        const difference = now.difference(other, 'days');
        const days = Math.abs(difference);
        if(Math.round(days) === 0) {
            return 0;
        } else {
            return Math.round(days) * (difference < 0 ? 1 : -1);
        }
    }

}
